import { listBlobs } from '~/storage/blobStore'
import { ALL_STORE_NAMES, clearAllStoresIn } from '~/storage/clearAll'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError, toStorageError } from '~/storage/errors'
import { notifyWritten } from '~/storage/invalidation'
import { estimateStorage } from '~/storage/quota'
import { asSchemaAgnostic, countAllStores, readAllStores } from '~/storage/readAll'
import { type RecoverySnapshotRef, writeSnapshot } from '~/storage/recoveryStore'
import { META_KEY, type StoreName } from '~/storage/schema'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { DEFAULT_SIZE_POLICY, preflightRestore, type SizePolicy } from './sizePolicy'
import type { ValidatedBackup } from './validateBackup'

/**
 * Replace-only restore (D-018, D-031).
 *
 * **There is no merge.** No merge option exists in this module or anywhere in
 * the UI, and none may be added in Version 1.
 *
 * Sequence, with nothing mutated before step 4:
 *
 *   1. (done by `validateBackup`) parse, verify, migrate, validate
 *   2. storage preflight — refuse rather than skip the safety copy
 *   3. write a DURABLE pre-restore snapshot, including blob bytes
 *   4. replace atomically in ONE transaction across every store
 *   5. verify counts against the manifest
 *   6. mark the restore as awaiting the user's confirmation
 */

export interface RestoreOutcome {
  readonly recordsRestored: number
  readonly blobsRestored: number
  readonly snapshot: RecoverySnapshotRef | undefined
  readonly migrated: boolean
}

export type RestoreError =
  | { readonly kind: 'restoreRefused'; readonly message: string }
  | { readonly kind: 'verificationFailed'; readonly message: string }
  | StorageError

export interface RestoreOptions {
  readonly policy?: SizePolicy
  /** Test seam. Production always takes the snapshot. */
  readonly skipSnapshot?: boolean
}

export async function restoreBackup(
  db: SolaDatabase,
  backup: ValidatedBackup,
  options: RestoreOptions = {},
): Promise<Result<RestoreOutcome, RestoreError>> {
  const policy = options.policy ?? DEFAULT_SIZE_POLICY

  try {
    // ── 2. Storage preflight ────────────────────────────────────────────────
    const currentBlobs = await listBlobs(db)
    if (!currentBlobs.ok) return err(currentBlobs.error)

    const currentBytes = currentBlobs.value.reduce((sum, entry) => sum + entry.blob.size, 0)
    const incomingBytes = [...backup.blobs.values()].reduce(
      (sum, bytes) => sum + bytes.byteLength,
      0,
    )
    const estimate = await estimateStorage()

    const preflight = preflightRestore(currentBytes, incomingBytes, estimate.availableBytes, policy)
    if (preflight.kind === 'refuse') {
      return err({ kind: 'restoreRefused', message: preflight.message })
    }

    // ── 3. Durable pre-restore snapshot ─────────────────────────────────────
    // Includes blob BYTES, unlike the pre-migration snapshot: a restore replaces
    // blobs, so metadata alone would not be recoverable (D-030).
    let snapshot: RecoverySnapshotRef | undefined
    if (options.skipSnapshot !== true) {
      const records = await readAllStores(asSchemaAgnostic(db), { includeBlobs: false })
      snapshot = await writeSnapshot({
        purpose: 'pre-restore',
        records,
        blobs: currentBlobs.value.map((entry) => ({
          blobId: entry.blobId,
          blob: entry.blob,
          mimeType: entry.mimeType,
        })),
      })
    }

    await db.put(STORE_META, { key: META_KEY.restoreState, value: 'in-progress' })

    // ── 4. Atomic replace ───────────────────────────────────────────────────
    const replaced = await replaceAll(db, backup)
    if (!replaced.ok) return replaced

    // ── 5. Verify against the manifest ──────────────────────────────────────
    const verified = await verifyCounts(db, backup)
    if (!verified.ok) return verified

    // ── 6. Await the user's confirmation ────────────────────────────────────
    await db.put(STORE_META, { key: META_KEY.restoreState, value: 'awaiting-confirmation' })
    notifyWritten(...(ALL_STORE_NAMES as StoreName[]))

    return ok({
      recordsRestored: replaced.value,
      blobsRestored: backup.manifest.blobs.length,
      snapshot,
      migrated: backup.migrated,
    })
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Clear and rewrite every store in ONE transaction.
 *
 * Everything needed is already in memory, so no non-IndexedDB work happens
 * inside the transaction (D-028). It commits completely or not at all.
 */
async function replaceAll(
  db: SolaDatabase,
  backup: ValidatedBackup,
): Promise<Result<number, RestoreError>> {
  const tx = db.transaction([...ALL_STORE_NAMES], 'readwrite')

  try {
    // The clear is shared with the full reset (storage/clearAll.ts) so a store
    // added to the schema cannot be wiped by one path and missed by the other.
    await clearAllStoresIn(tx)

    let recordsRestored = 0
    for (const [store, rows] of Object.entries(backup.payload.stores)) {
      if (store === 'blobs') continue
      const objectStore = tx.objectStore(store as StoreName)
      for (const row of rows) {
        await objectStore.put(row as never)
        recordsRestored += 1
      }
    }

    const blobStore = tx.objectStore('blobs')
    for (const entry of backup.manifest.blobs) {
      const bytes = backup.blobs.get(entry.blobId)
      if (bytes === undefined) throw new Error(`missing blob ${entry.blobId} at write time`)
      await blobStore.put({
        blobId: entry.blobId,
        blob: new Blob([bytes as BlobPart], { type: entry.mimeType }),
        mimeType: entry.mimeType,
        byteSize: entry.byteSize,
      })
    }

    await tx.done
    return ok(recordsRestored)
  } catch (cause) {
    try {
      tx.abort()
    } catch {
      // Already settled; the original failure is what matters.
    }
    void tx.done.catch(() => undefined)
    return err(
      storageError(
        'unknown',
        'The restore did not complete, so nothing was changed. Your existing data is intact.',
        cause,
      ),
    )
  }
}

async function verifyCounts(
  db: SolaDatabase,
  backup: ValidatedBackup,
): Promise<Result<true, RestoreError>> {
  const counts = await countAllStores(asSchemaAgnostic(db))

  for (const [store, expected] of Object.entries(backup.manifest.recordCounts)) {
    const actual = counts[store] ?? 0
    if (actual !== expected) {
      return err({
        kind: 'verificationFailed',
        message: `After restoring, ${store} holds ${actual} records but the backup recorded ${expected}. Your previous data is still available from the recovery copy.`,
      })
    }
  }

  return ok(true)
}

const STORE_META = 'meta' as const

/** Called once the user confirms the restored data looks right (D-030). */
export async function confirmRestore(db: SolaDatabase): Promise<Result<void, StorageError>> {
  try {
    await db.delete(STORE_META, META_KEY.restoreState)
    return ok(undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function getRestoreState(db: SolaDatabase): Promise<string | undefined> {
  try {
    const row = await db.get(STORE_META, META_KEY.restoreState)
    return row?.value as string | undefined
  } catch {
    return undefined
  }
}
